"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ShoppingCart, MousePointerClick, Percent } from "lucide-react";

const SOURCES = [
  {
    icon: ShoppingCart,
    title: { en: "Ticket sale via link", es: "Venta por link" },
    sub: { en: "Maria opened Sofia's link → bought GA $40. Attributed to Sofia.", es: "Maria abrió el link de Sofia → compró GA $40. Atribuido a Sofia." },
    count: "87",
  },
  {
    icon: MousePointerClick,
    title: { en: "Clicks and conversion", es: "Clicks y conversión" },
    sub: { en: "See how many people opened each promoter's link and how many of them bought.", es: "Ves cuánta gente abrió el link de cada promotor y cuántos compraron." },
    count: "412",
  },
  {
    icon: Percent,
    title: { en: "Commission per sale", es: "Comisión por venta" },
    sub: { en: "Flat $ per ticket or a % of the ticket. Redy calculates it on every attributed sale.", es: "Fija por entrada o % del ticket. Redy la calcula en cada venta atribuida." },
    count: "$522",
  },
];

export default function PromoterAttributionScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Attribution", "Atribución")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Every sale. ", "Cada venta. ")}
            <span className="text-white/30">{t("On the record.", "En el registro.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Every ticket bought through a promoter's link is tagged to them, with clicks, conversion and commission alongside. End of night, every dollar tagged to a promoter is auditable.",
              "Cada entrada comprada por el link de un promotor queda a su nombre, con clicks, conversión y comisión al lado. Al cerrar la noche, cada dólar tageado a un promotor es auditable."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto">
          {SOURCES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-6 relative"
              >
                <div className="absolute top-5 right-5 text-[28px] font-black tabular-nums" style={{
                  background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}>
                  {s.count}
                </div>
                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(124,58,237,0.2)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: "#C4B5FD" }} />
                </div>
                <div className="text-[16px] font-black text-white mb-2 tracking-tight pr-12">
                  {t(s.title.en, s.title.es)}
                </div>
                <div className="text-[13px] font-medium text-white/55 leading-relaxed">
                  {t(s.sub.en, s.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
